import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loading, fmtDate } from '@/components/OpsUI';
import { Inbox, Send, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { AiMeta, StatusPill, SUGGESTION_STATUS } from '@/components/ai/AiCommon';
import { LearnButton } from '@/components/ai/LearnButton';

const DraftCard = ({ s, canAct, onDone }) => {
  const navigate = useNavigate();
  const [text, setText] = useState(s.final_message || s.reply || '');
  const [busy, setBusy] = useState(false);
  const isDraft = s.status === 'draft';

  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === 'send') await api.post(`/ai/suggestions/${s.id}/send`, { message: text });
      else await api.post(`/ai/suggestions/${s.id}/discard`);
      toast.success(kind === 'send' ? 'Respuesta enviada al pastor' : 'Borrador descartado');
      onDone();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid={`ai-draft-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium truncate">{s.ticket_subject || `Ticket ${s.ticket_id}`}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            {s.church || '—'} · {s.source === 'auto' ? 'Automático (webhook)' : `Manual · ${s.requested_by || ''}`} · {fmtDate(s.created_at)}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusPill status={s.status} />
          <Button size="sm" variant="ghost" onClick={() => navigate(`/app-tickets?ticket=${s.ticket_id}`)} data-testid={`ai-draft-open-${s.id}`}><ExternalLink className="w-4 h-4" /></Button>
        </div>
      </div>
      {s.status === 'error' ? <p className="text-sm text-red-300">{s.error}</p> : <AiMeta result={s} />}
      {s.status !== 'error' && (isDraft && canAct ? (
        <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid={`ai-draft-text-${s.id}`} />
      ) : (
        <div className="text-sm whitespace-pre-wrap rounded-md bg-[hsl(var(--muted))]/20 border border-border p-3">{text}</div>
      ))}
      {canAct && ['sent', 'auto_sent'].includes(s.status) && (
        <div className="flex justify-end">
          <LearnButton message={text} ticketId={s.ticket_id} subject={s.ticket_subject} suggestionId={s.id} testid={`ai-draft-learn-${s.id}`} />
        </div>
      )}
      {isDraft && canAct && (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act('discard')} data-testid={`ai-draft-discard-${s.id}`}><Trash2 className="w-4 h-4 mr-2" />Descartar</Button>
          <Button size="sm" disabled={busy || !text.trim()} onClick={() => act('send')} data-testid={`ai-draft-send-${s.id}`}><Send className="w-4 h-4 mr-2" />Aprobar y enviar</Button>
        </div>
      )}
    </Card>
  );
};

export const AiDrafts = ({ canAct, onChange }) => {
  const [status, setStatus] = useState('draft');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (status !== 'all') params.status = status;
      const { data } = await api.get('/ai/suggestions', { params });
      setItems(data || []);
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const done = () => { load(); onChange?.(); };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-sm text-[hsl(var(--muted-foreground))]">Mostrar</span>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" data-testid="ai-drafts-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(SUGGESTION_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading ? <Loading /> : items.length === 0 ? (
        <Card className="p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="ai-drafts-empty">
          <Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No hay elementos en esta vista</p>
          <p className="text-sm mt-1">Cuando llegue un ticket nuevo, el asistente IA lo analizará automáticamente.</p>
        </Card>
      ) : items.map((s) => <DraftCard key={s.id} s={s} canAct={canAct} onDone={done} />)}
    </div>
  );
};
